import debounce from 'lodash.debounce'
import ChaptersService from '@/services/chapters'
import BackupService from '@/services/backup'
import type { Chapter } from '@/types'
import type { ChapterBackup } from '@/types/editor'
import {
  EDITOR_POST_SAVE_VERIFY_DELAY_MS,
  LOCAL_PERSIST_DEBOUNCE_MAX_WAIT_MS,
} from '@/constants/editorNetwork'
import {
  describeChapterSyncFailure,
  getChapterFromVersionConflictError,
  isChapterContentDeletionRequiresForceError,
  isChapterVersionConflictError,
  logChapterContentVersionConflict,
  CHAPTER_POSSIBLE_VERSION_CONFLICT,
} from '@/utils/chapterErrors'
import { isLenientBackgroundChapterSync } from '@/utils/chapterBackgroundSyncPolicy'
import { isConnectivityError } from '@/utils/isConnectivityError'
import { isAmbiguousSaveFailure } from '@/utils/isAmbiguousSaveFailure'
import { getHttpErrorStatus } from '@/utils/httpErrorStatus'
import {
  appendInDoubtSaveId,
  createChapterSaveId,
  normalizeContentVersion,
  readServerContentVersion,
  readServerLastContentSaveId,
} from '@/utils/chapterContentVersion'
import {
  allocateChapterNextVersion,
  ensureChapterNextVersionCounter,
  resetChapterNextVersionCounter,
} from '@/utils/chapterNextVersion'
import {
  captureChapterSaveDiagnostic,
  clearChapterSaveDiagnosticReports,
  shouldCaptureChapterSaveDiagnostic,
} from '@/utils/chapterSaveSentryContext'
import {
  isBackupPendingUpload,
  isQueueEligibleSyncPhase,
  resolveSyncPhase,
  type ChapterSyncPhase,
} from '@/utils/chapterSyncPhase'
import { useConnectionStatus } from '@/composables/useConnectionStatus'

const RETRY_DELAY_BASE_MS = 2000
const RETRY_DELAY_MAX_MS = 30000
const MAX_RETRY_ATTEMPTS = 5
const DEFAULT_UPLOAD_DEBOUNCE_MS = 1000
const LOCAL_PERSIST_DEBOUNCE_MS = 300
const LOCK_BUSY_RETRY_MS = 500

const doubtfulSaveChapterKeys = new Set<string>()
const lastConfirmedContentHashByChapter = new Map<string, string>()
const postSaveVerifyTimers = new Map<string, ReturnType<typeof setTimeout>>()
const foregroundUploadTimers = new Map<string, ReturnType<typeof setTimeout>>()

function getServerContentHash(chapter: Chapter | undefined): string | undefined {
  const hash = (chapter as Chapter & { contentHash?: string | null })?.contentHash
  return typeof hash === 'string' && hash.length > 0 ? hash : undefined
}

function clearPostSaveVerifyTimer(key: string): void {
  const timer = postSaveVerifyTimers.get(key)
  if (timer) {
    clearTimeout(timer)
    postSaveVerifyTimers.delete(key)
  }
}

function markChapterSaveDoubtful(projectId: string, chapterId: string): void {
  doubtfulSaveChapterKeys.add(chapterKey(projectId, chapterId))
}

function schedulePostSaveContentVerification(
  projectId: string,
  chapterId: string,
): void {
  const key = chapterKey(projectId, chapterId)
  clearPostSaveVerifyTimer(key)
  const confirmedHash = lastConfirmedContentHashByChapter.get(key)
  if (!confirmedHash) {
    return
  }

  const timer = setTimeout(() => {
    postSaveVerifyTimers.delete(key)
    void verifyServerContentHashAfterSave(projectId, chapterId)
  }, EDITOR_POST_SAVE_VERIFY_DELAY_MS)
  postSaveVerifyTimers.set(key, timer)
}

async function verifyServerContentHashAfterSave(
  projectId: string,
  chapterId: string,
): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return
  }

  const key = chapterKey(projectId, chapterId)
  const expectedHash = lastConfirmedContentHashByChapter.get(key)
  if (!expectedHash) {
    return
  }

  try {
    const response = await ChaptersService.get(chapterId)
    const serverChapter = response.data as Chapter
    const serverHash = getServerContentHash(serverChapter)
    if (!serverHash || serverHash === expectedHash) {
      return
    }

    const backup = BackupService.getChapterBackup(projectId, chapterId)
    if (!backup) {
      if (shouldCaptureChapterSaveDiagnostic(chapterId)) {
        captureChapterSaveDiagnostic('content_hash_mismatch_no_backup', {
          chapterId,
          projectId,
          navigatorOnLine: true,
        })
      }
      return
    }

    BackupService.patchChapterBackup(projectId, chapterId, {
      syncPhase: 'dirty',
      pendingSync: true,
      bumpTimestamp: true,
    })
    notifyListeners()

    const latest = BackupService.getChapterBackup(projectId, chapterId)
    if (!latest) {
      return
    }

    if (isActiveEditorChapter(projectId, chapterId)) {
      void chapterPersistenceOrchestrator.uploadChapter(latest, {
        allowWhileActiveEditor: true,
        silentConnectivityError: true,
      })
      return
    }

    chapterPersistenceOrchestrator.scheduleUpload(projectId, chapterId, {
      delayMs: 0,
      force: true,
    })
  } catch (error) {
    console.error('Post-save content verification failed:', error)
  }
}

function noteSuccessfulChapterSave(
  projectId: string,
  chapterId: string,
  serverChapter: Chapter,
): void {
  const key = chapterKey(projectId, chapterId)
  clearPostSaveVerifyTimer(key)
  clearChapterSaveDiagnosticReports(chapterId)

  const hash = getServerContentHash(serverChapter)
  if (hash) {
    lastConfirmedContentHashByChapter.set(key, hash)
  }

  if (!doubtfulSaveChapterKeys.has(key)) {
    return
  }

  doubtfulSaveChapterKeys.delete(key)
  schedulePostSaveContentVerification(projectId, chapterId)
}

function clearForegroundUploadTimer(key: string): void {
  const timer = foregroundUploadTimers.get(key)
  if (timer) {
    clearTimeout(timer)
    foregroundUploadTimers.delete(key)
  }
}

async function runForegroundUploadRetry(projectId: string, chapterId: string): Promise<void> {
  const backup = BackupService.getChapterBackup(projectId, chapterId)
  if (!backup || !isBackupPendingUpload(backup as ChapterBackup & { syncPhase?: ChapterSyncPhase })) {
    notifyListeners()
    return
  }

  const result = await chapterPersistenceOrchestrator.uploadChapter(backup, {
    allowWhileActiveEditor: true,
    silentConnectivityError: true,
  })
  notifyListeners()

  if (
    result.status === 'failed'
    && result.message === 'Guardado en curso.'
    && isActiveEditorChapter(projectId, chapterId)
  ) {
    enqueueForegroundUploadRetry(projectId, chapterId)
  }
}

function enqueueForegroundUploadRetry(projectId: string, chapterId: string): void {
  const key = chapterKey(projectId, chapterId)
  clearForegroundUploadTimer(key)

  const state = getWorkerState(projectId, chapterId)
  if (state.exhausted || state.attempts >= MAX_RETRY_ATTEMPTS) {
    return
  }

  const delayMs = Math.max(LOCK_BUSY_RETRY_MS, getBackoffDelayMs(state))
  const timer = setTimeout(() => {
    foregroundUploadTimers.delete(key)
    void runForegroundUploadRetry(projectId, chapterId)
  }, delayMs)
  foregroundUploadTimers.set(key, timer)
}

function maybeCaptureAmbiguousSaveDiagnostic(
  backup: ChapterBackup,
  error: unknown,
): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return
  }
  if (!isAmbiguousSaveFailure(error)) {
    return
  }
  if (!shouldCaptureChapterSaveDiagnostic(backup.chapterId)) {
    return
  }
  captureChapterSaveDiagnostic('patch_timeout_online', {
    chapterId: backup.chapterId,
    projectId: backup.projectId,
    navigatorOnLine: true,
  })
}

function handleAmbiguousPatchFailure(
  backup: ChapterBackup,
  error: unknown,
  options: UploadOptions,
): UploadChapterResult {
  markChapterSaveDoubtful(backup.projectId, backup.chapterId)
  recordFailedAttempt(backup.projectId, backup.chapterId)
  BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
    syncPhase: 'dirty',
    pendingSync: true,
    lastErrorCode: 'AMBIGUOUS_SAVE',
    lastErrorMessage: describeChapterSyncFailure(error),
  })
  notifyListeners()
  maybeCaptureAmbiguousSaveDiagnostic(backup, error)

  if (options.allowWhileActiveEditor) {
    enqueueForegroundUploadRetry(backup.projectId, backup.chapterId)
  } else {
    scheduleWorker(getBackoffDelayMs(getWorkerState(backup.projectId, backup.chapterId)))
  }

  return {
    status: 'failed',
    error,
    message: describeChapterSyncFailure(error),
  }
}

export type ChapterUploadAutoSavePolicy = {
  autoSave: boolean
  autoSaveFrequency: 0 | 30 | 60 | 120 | 300
}

export type PersistLocalDraftInput = {
  projectId: string
  chapterId: string
  title: string
  jsonContent?: object
  content?: string
  userId?: string
  baseUpdatedAt?: string
  baseContentVersion?: number
  inDoubtSaveIds?: string[]
  order?: number
  patchScope?: 'title' | 'full'
}

export type UploadChapterResult =
  | { status: 'success'; chapter: Chapter }
  | { status: 'noop' }
  | { status: 'editor_moved'; chapter?: Chapter }
  | { status: 'conflict'; serverChapter?: Chapter }
  | { status: 'needs_editor' }
  | { status: 'auth_error' }
  | { status: 'offline' }
  | { status: 'failed'; error: unknown; message: string }

type UploadOptions = {
  force?: boolean
  silentConnectivityError?: boolean
  expectedUpdatedAt?: string
  titleOnly?: boolean
  /** Foreground save from the open editor (skips background-only guard). */
  allowWhileActiveEditor?: boolean
  saveId?: string
  retryingLostAck?: boolean
}

type WorkerState = {
  attempts: number
  lastAttempt: number
  exhausted: boolean
  /** Backoff window fixed when the attempt was recorded (avoids jitter drift). */
  backoffMs?: number
}

type OrchestratorListener = () => void

type ActiveEditorChapter = {
  projectId: string
  chapterId: string
}

const workerStateByKey = new Map<string, WorkerState>()
const inFlightUploadRefCount = new Map<string, number>()
let trackedSessionUserIdForAuthRelease: string | undefined

function incrementInFlightUpload(key: string): void {
  inFlightUploadRefCount.set(key, (inFlightUploadRefCount.get(key) ?? 0) + 1)
}

function decrementInFlightUpload(key: string): void {
  const next = (inFlightUploadRefCount.get(key) ?? 1) - 1
  if (next <= 0) {
    inFlightUploadRefCount.delete(key)
  } else {
    inFlightUploadRefCount.set(key, next)
  }
}

function isInFlightUpload(key: string): boolean {
  return (inFlightUploadRefCount.get(key) ?? 0) > 0
}
const uploadTimers = new Map<string, ReturnType<typeof setTimeout>>()
const listeners = new Set<OrchestratorListener>()
let workerRunning = false
let workerTimer: ReturnType<typeof setTimeout> | null = null
let onlineWatchInstalled = false
let activeEditorChapter: ActiveEditorChapter | null = null

const defaultAutoSavePolicy = (): ChapterUploadAutoSavePolicy => ({
  autoSave: true,
  autoSaveFrequency: 0,
})

let getAutoSavePolicy: () => ChapterUploadAutoSavePolicy = defaultAutoSavePolicy

function chapterKey(projectId: string, chapterId: string): string {
  return `${projectId}:${chapterId}`
}

function isAppOnline(): boolean {
  if (typeof window === 'undefined') return true
  const { isOnline } = useConnectionStatus()
  return isOnline.value
}

function notifyListeners(): void {
  listeners.forEach((listener) => {
    try {
      listener()
    } catch (error) {
      console.error('Chapter persistence listener failed:', error)
    }
  })
}

function calculateBackoffDelay(attempts: number): number {
  const exponential = RETRY_DELAY_BASE_MS * Math.pow(2, attempts)
  const jitter = Math.random() * 1000
  return Math.min(exponential + jitter, RETRY_DELAY_MAX_MS)
}

function getWorkerState(projectId: string, chapterId: string): WorkerState {
  const key = chapterKey(projectId, chapterId)
  let state = workerStateByKey.get(key)
  if (!state) {
    state = { attempts: 0, lastAttempt: 0, exhausted: false }
    workerStateByKey.set(key, state)
  }
  return state
}

function resetWorkerState(projectId: string, chapterId: string): void {
  workerStateByKey.delete(chapterKey(projectId, chapterId))
}

function markWorkerExhausted(projectId: string, chapterId: string): void {
  const state = getWorkerState(projectId, chapterId)
  state.exhausted = true
  state.lastAttempt = Date.now()
}

function getBackoffDelayMs(state: WorkerState): number {
  return state.backoffMs ?? calculateBackoffDelay(state.attempts)
}

function shouldAttemptUpload(state: WorkerState): boolean {
  if (state.exhausted) return false
  if (state.attempts >= MAX_RETRY_ATTEMPTS) return false
  return Date.now() - state.lastAttempt >= getBackoffDelayMs(state)
}

function isWaitingForUploadBackoff(state: WorkerState): boolean {
  if (state.exhausted || state.attempts >= MAX_RETRY_ATTEMPTS) return false
  if (state.attempts === 0) return false
  return !shouldAttemptUpload(state)
}

function shouldScheduleAutomaticUpload(): boolean {
  const policy = getAutoSavePolicy()
  if (!policy.autoSave) return false
  if (policy.autoSaveFrequency > 0) return false
  return true
}

function scheduleWorker(delayMs = 0): void {
  if (workerTimer !== null) return
  workerTimer = setTimeout(() => {
    workerTimer = null
    void runUploadWorker()
  }, delayMs)
}

function recordFailedAttempt(projectId: string, chapterId: string): void {
  const state = getWorkerState(projectId, chapterId)
  state.lastAttempt = Date.now()
  state.attempts += 1
  state.backoffMs = calculateBackoffDelay(state.attempts)
  if (state.attempts >= MAX_RETRY_ATTEMPTS) {
    state.exhausted = true
  }
}

function listQueueRecords(): ChapterBackup[] {
  return BackupService.listChapterBackups().filter((backup) => {
    if (isActiveEditorChapter(backup.projectId, backup.chapterId)) {
      return false
    }
    const phase = resolveSyncPhase(backup as ChapterBackup & { syncPhase?: ChapterSyncPhase })
    return isQueueEligibleSyncPhase(phase)
  })
}

function isActiveEditorChapter(projectId: string, chapterId: string): boolean {
  return (
    activeEditorChapter?.projectId === projectId
    && activeEditorChapter?.chapterId === chapterId
  )
}

function isBackgroundUploadBlockedPhase(phase: ChapterSyncPhase): boolean {
  return phase === 'auth_blocked' || phase === 'offline' || phase === 'conflict' || phase === 'needs_editor'
}

function isRecoverableConnectivityFailedPhase(
  backup: ChapterBackup,
  phase: ChapterSyncPhase,
): boolean {
  if (phase !== 'failed') return false
  return backup.lastErrorCode === 'CONNECTIVITY' || backup.lastErrorCode === 'AMBIGUOUS_SAVE'
}

function requeueActiveEditorChapterForUpload(
  projectId: string,
  chapterId: string,
): ChapterBackup | null {
  const backup = BackupService.getChapterBackup(projectId, chapterId)
  if (!backup || !isBackupPendingUpload(backup as ChapterBackup & { syncPhase?: ChapterSyncPhase })) {
    return null
  }

  const phase = resolveSyncPhase(backup as ChapterBackup & { syncPhase?: ChapterSyncPhase })
  if (phase === 'auth_blocked' || phase === 'needs_editor' || phase === 'conflict') {
    return null
  }

  if (phase === 'offline' || isRecoverableConnectivityFailedPhase(backup, phase)) {
    BackupService.patchChapterBackup(projectId, chapterId, {
      syncPhase: 'dirty',
      lastErrorCode: undefined,
      lastErrorMessage: undefined,
    })
    resetWorkerState(projectId, chapterId)
    notifyListeners()
  }

  return BackupService.getChapterBackup(projectId, chapterId)
}

export function resumePendingUploadForOpenChapter(
  projectId: string,
  chapterId: string,
): void {
  if (!isAppOnline()) return
  if (!isActiveEditorChapter(projectId, chapterId)) return

  const requeued = requeueActiveEditorChapterForUpload(projectId, chapterId)
  if (!requeued) return

  enqueueForegroundUploadRetry(projectId, chapterId)
}

type StrictBackgroundContentCheck =
  | { kind: 'ok' }
  | { kind: 'conflict'; serverChapter: Chapter }
  | { kind: 'auth_error'; error: unknown; status: 401 | 403 }
  | { kind: 'terminal_failure'; error: unknown }
  | { kind: 'retryable_failure'; error: unknown }

function serverSaveAcknowledgedByBackup(
  backup: ChapterBackup,
  serverChapter: Chapter,
): boolean {
  const lostSaveId = readServerLastContentSaveId(serverChapter)
  if (!lostSaveId) return false
  return Boolean(backup.inDoubtSaveIds?.includes(lostSaveId))
}

async function detectStrictBackgroundContentConflict(
  backup: ChapterBackup,
): Promise<StrictBackgroundContentCheck> {
  try {
    const response = await ChaptersService.get(backup.chapterId)
    const serverChapter = response.data as Chapter
    const serverVersion = readServerContentVersion(serverChapter)
    const baseVersion = normalizeContentVersion(backup.baseContentVersion)

    if (serverVersion <= baseVersion) {
      return { kind: 'ok' }
    }

    if (serverSaveAcknowledgedByBackup(backup, serverChapter)) {
      return { kind: 'ok' }
    }

    return { kind: 'conflict', serverChapter }
  } catch (error) {
    const status = getHttpErrorStatus(error)
    if (status === 401 || status === 403) {
      return { kind: 'auth_error', error, status }
    }
    if (status === 404) {
      return { kind: 'terminal_failure', error }
    }
    return { kind: 'retryable_failure', error }
  }
}

function markBackupPossibleVersionConflict(
  projectId: string,
  chapterId: string,
): void {
  BackupService.patchChapterBackup(projectId, chapterId, {
    syncPhase: 'conflict',
    pendingSync: true,
    lastErrorCode: CHAPTER_POSSIBLE_VERSION_CONFLICT,
    lastErrorMessage: describeChapterSyncFailure({
      response: { data: { reason: CHAPTER_POSSIBLE_VERSION_CONFLICT } },
    }),
  })
  notifyListeners()
}

function handleStrictContentCheckFailure(
  backup: ChapterBackup,
  check: Exclude<StrictBackgroundContentCheck, { kind: 'ok' } | { kind: 'conflict' }>,
): UploadChapterResult {
  const { projectId, chapterId } = backup

  if (check.kind === 'auth_error') {
    markWorkerExhausted(projectId, chapterId)
    BackupService.patchChapterBackup(projectId, chapterId, {
      syncPhase: 'auth_blocked',
      lastErrorCode: check.status === 401 ? 'UNAUTHORIZED_ERROR' : 'FORBIDDEN_ERROR',
      lastErrorMessage: describeChapterSyncFailure(check.error),
    })
    notifyListeners()
    return { status: 'auth_error' }
  }

  if (check.kind === 'terminal_failure') {
    markWorkerExhausted(projectId, chapterId)
    BackupService.patchChapterBackup(projectId, chapterId, {
      syncPhase: 'failed',
      lastErrorCode: 'NOT_FOUND_ERROR',
      lastErrorMessage: describeChapterSyncFailure(check.error),
    })
    notifyListeners()
    return {
      status: 'failed',
      error: check.error,
      message: describeChapterSyncFailure(check.error),
    }
  }

  recordFailedAttempt(projectId, chapterId)
  const state = getWorkerState(projectId, chapterId)
  const message = describeChapterSyncFailure(check.error)
  BackupService.patchChapterBackup(projectId, chapterId, {
    syncPhase: state.exhausted ? 'failed' : 'dirty',
    lastErrorCode: 'CONNECTIVITY',
    lastErrorMessage: message,
  })
  notifyListeners()
  if (!state.exhausted) {
    scheduleWorker(getBackoffDelayMs(state))
  }
  return { status: 'failed', error: check.error, message }
}

function shouldRunStrictBackgroundVersionGuard(
  backup: ChapterBackup,
  options: UploadOptions,
): boolean {
  if (options.allowWhileActiveEditor) return false
  if (isTitleOnlyUpload(backup, options)) return false
  return !isLenientBackgroundChapterSync(backup)
}

function resetReconnectEligiblePhasesToDirty(): void {
  for (const backup of BackupService.listChapterBackups()) {
    const phase = resolveSyncPhase(backup as ChapterBackup & { syncPhase?: ChapterSyncPhase })
    const key = chapterKey(backup.projectId, backup.chapterId)
    if (phase === 'offline') {
      BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
        syncPhase: 'dirty',
        lastErrorCode: undefined,
        lastErrorMessage: undefined,
      })
      resetWorkerState(backup.projectId, backup.chapterId)
    }
    if (phase === 'uploading' && !isInFlightUpload(key)) {
      BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
        syncPhase: 'dirty',
      })
      resetWorkerState(backup.projectId, backup.chapterId)
    }
  }
}

export function clearAuthReleaseSessionTracking(): void {
  trackedSessionUserIdForAuthRelease = undefined
}

/**
 * Re-queue auth-blocked backups after login or token refresh.
 * `treatAsLogin`: release all (post-logout login). Otherwise same user → only 401; user switch → all.
 */
export function releaseAuthBlockedChapterBackups(
  sessionUserId?: string,
  options: { treatAsLogin?: boolean } = {},
): void {
  if (!sessionUserId) return

  const priorUserId = trackedSessionUserIdForAuthRelease
  const releaseAll =
    options.treatAsLogin === true
    || (priorUserId !== undefined && priorUserId !== sessionUserId)
  trackedSessionUserIdForAuthRelease = sessionUserId

  let released = false
  for (const backup of BackupService.listChapterBackups()) {
    const phase = resolveSyncPhase(backup as ChapterBackup & { syncPhase?: ChapterSyncPhase })
    if (phase !== 'auth_blocked') continue
    if (!releaseAll && backup.lastErrorCode !== 'UNAUTHORIZED_ERROR') continue

    BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
      syncPhase: 'dirty',
      lastErrorCode: undefined,
      lastErrorMessage: undefined,
    })
    resetWorkerState(backup.projectId, backup.chapterId)
    released = true
  }
  if (released) {
    scheduleWorker(0)
    notifyListeners()
  }
}

async function runUploadWorker(): Promise<void> {
  if (workerRunning) return
  if (!isAppOnline()) return

  workerRunning = true
  try {
    const queue = listQueueRecords()
    for (const backup of queue) {
      const state = getWorkerState(backup.projectId, backup.chapterId)
      if (!shouldAttemptUpload(state)) continue
      await chapterPersistenceOrchestrator.uploadChapter(backup)
    }
  } finally {
    workerRunning = false
  }

  const hasRetryable = listQueueRecords().some((backup) => {
    const state = getWorkerState(backup.projectId, backup.chapterId)
    return shouldAttemptUpload(state) || isWaitingForUploadBackoff(state)
  })
  if (hasRetryable) {
    const nextDelay = listQueueRecords()
      .map((backup) => {
        const state = getWorkerState(backup.projectId, backup.chapterId)
        const delay = getBackoffDelayMs(state)
        const elapsed = Date.now() - state.lastAttempt
        return Math.max(0, delay - elapsed)
      })
      .reduce((min, value) => Math.min(min, value), RETRY_DELAY_MAX_MS)
    scheduleWorker(nextDelay)
  }
}

function installOnlineWatcher(): void {
  if (onlineWatchInstalled || typeof window === 'undefined') return
  onlineWatchInstalled = true
  window.addEventListener('online', () => {
    for (const state of workerStateByKey.values()) {
      if (!state.exhausted) {
        state.attempts = 0
        state.lastAttempt = 0
      }
    }
    resetReconnectEligiblePhasesToDirty()
    chapterPersistenceOrchestrator.reconcile()
  })
}

function isTitleOnlyUpload(backup: ChapterBackup, options: UploadOptions): boolean {
  if (backup.patchScope === 'full') {
    return false
  }
  return options.titleOnly === true || backup.patchScope === 'title'
}

function buildPatchPayload(
  backup: ChapterBackup,
  options: UploadOptions,
  saveId: string,
  titleOnly: boolean,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    title: backup.title,
    expectedUpdatedAt: options.expectedUpdatedAt ?? backup.baseUpdatedAt,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }
  if (!titleOnly) {
    payload.jsonContent = backup.jsonContent || {}
    ensureChapterNextVersionCounter(
      backup.projectId,
      backup.chapterId,
      normalizeContentVersion(backup.baseContentVersion),
    )
    payload.nextVersion = allocateChapterNextVersion(backup.projectId, backup.chapterId)
    payload.saveId = saveId
  }
  if (options.force) {
    payload.force = true
  }
  return payload
}

export function subscribeChapterPersistence(listener: OrchestratorListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function cancelScheduledUpload(projectId: string, chapterId: string): void {
  const key = chapterKey(projectId, chapterId)
  const existing = uploadTimers.get(key)
  if (existing) {
    clearTimeout(existing)
    uploadTimers.delete(key)
  }
}

export function setChapterUploadAutoSavePolicy(
  getter: () => ChapterUploadAutoSavePolicy,
): void {
  getAutoSavePolicy = getter
}

export function setActiveEditorChapter(scope: ActiveEditorChapter | null): void {
  activeEditorChapter = scope
}

export function getActiveEditorChapter(): ActiveEditorChapter | null {
  return activeEditorChapter
}

export function resetChapterPersistenceOrchestratorForTests(): void {
  workerStateByKey.clear()
  inFlightUploadRefCount.clear()
  clearAuthReleaseSessionTracking()
  doubtfulSaveChapterKeys.clear()
  lastConfirmedContentHashByChapter.clear()
  for (const timer of postSaveVerifyTimers.values()) {
    clearTimeout(timer)
  }
  postSaveVerifyTimers.clear()
  for (const timer of foregroundUploadTimers.values()) {
    clearTimeout(timer)
  }
  foregroundUploadTimers.clear()
  for (const timer of uploadTimers.values()) {
    clearTimeout(timer)
  }
  uploadTimers.clear()
  workerRunning = false
  if (workerTimer !== null) {
    clearTimeout(workerTimer)
    workerTimer = null
  }
  getAutoSavePolicy = defaultAutoSavePolicy
  activeEditorChapter = null
}

export const chapterPersistenceOrchestrator = {
  persistLocalDraft(input: PersistLocalDraftInput): ChapterBackup | null {
    const projectAvailableOffline = BackupService.isProjectAvailableOffline(input.projectId)
    const existing = BackupService.getChapterBackup(input.projectId, input.chapterId)
    const patchScope =
      input.patchScope === 'full'
        ? 'full'
        : input.patchScope === 'title'
          ? existing?.patchScope === 'full'
            ? 'full'
            : 'title'
          : existing?.patchScope

    const saved = BackupService.saveChapterBackup({
      title: input.title || '',
      content: input.content,
      jsonContent: input.jsonContent || {},
      projectId: input.projectId,
      chapterId: input.chapterId,
      userId: input.userId,
      baseUpdatedAt: input.baseUpdatedAt,
      baseContentVersion: input.baseContentVersion,
      inDoubtSaveIds: input.inDoubtSaveIds,
      pendingSync: true,
      syncPhase: 'dirty',
      retained: projectAvailableOffline,
      order: input.order,
      patchScope,
    })
    resetWorkerState(input.projectId, input.chapterId)
    notifyListeners()
    return saved ? BackupService.getChapterBackup(input.projectId, input.chapterId) : null
  },

  scheduleUpload(
    projectId: string,
    chapterId: string,
    options?: { delayMs?: number; force?: boolean; includeActiveEditor?: boolean },
  ): void {
    if (!options?.force && !shouldScheduleAutomaticUpload()) {
      return
    }

    if (
      isActiveEditorChapter(projectId, chapterId)
      && !options?.includeActiveEditor
    ) {
      return
    }

    const delayMs = options?.delayMs ?? DEFAULT_UPLOAD_DEBOUNCE_MS
    const key = chapterKey(projectId, chapterId)
    const existing = uploadTimers.get(key)
    if (existing) {
      clearTimeout(existing)
    }
    const timer = setTimeout(() => {
      uploadTimers.delete(key)
      void chapterPersistenceOrchestrator
        .uploadChapter(BackupService.getChapterBackup(projectId, chapterId), {
          allowWhileActiveEditor: Boolean(options?.includeActiveEditor),
          silentConnectivityError: true,
          force: options?.force,
        })
        .then(() => {
          notifyListeners()
        })
    }, delayMs)
    uploadTimers.set(key, timer)
  },

  scheduleForegroundUploadRetry(projectId: string, chapterId: string): void {
    enqueueForegroundUploadRetry(projectId, chapterId)
  },

  flushChapter(projectId: string, chapterId: string): void {
    const key = chapterKey(projectId, chapterId)
    const pendingTimer = uploadTimers.get(key)
    if (pendingTimer) {
      clearTimeout(pendingTimer)
      uploadTimers.delete(key)
    }
    chapterPersistenceOrchestrator.scheduleUpload(projectId, chapterId, {
      delayMs: 0,
      force: true,
    })
  },

  reconcile(): void {
    installOnlineWatcher()
    if (isAppOnline()) {
      resetReconnectEligiblePhasesToDirty()
    }
    const records = BackupService.listChapterBackups()
    for (const backup of records) {
      const phase = resolveSyncPhase(backup as ChapterBackup & { syncPhase?: ChapterSyncPhase })
      if (isQueueEligibleSyncPhase(phase)) {
        resetWorkerState(backup.projectId, backup.chapterId)
      }
    }
    scheduleWorker(0)
    notifyListeners()
  },

  markSyncedGrace(
    projectId: string,
    chapterId: string,
    serverChapter: Chapter,
    options?: { preserveLocalContent?: boolean },
  ): ChapterBackup | null {
    const now = Date.now()
    const existing = BackupService.getChapterBackup(projectId, chapterId)
    const preserveLocalContent = options?.preserveLocalContent === true
    const stillPendingFull = preserveLocalContent && existing?.patchScope === 'full'
    const serverVersion = readServerContentVersion(serverChapter)
    const patched = BackupService.patchChapterBackup(projectId, chapterId, {
      title: serverChapter.title,
      jsonContent: preserveLocalContent
        ? existing?.jsonContent || serverChapter.jsonContent || {}
        : serverChapter.jsonContent || {},
      content: preserveLocalContent ? existing?.content : serverChapter.content,
      baseUpdatedAt: serverChapter.updatedAt,
      baseContentVersion: serverVersion,
      inDoubtSaveIds: stillPendingFull ? existing?.inDoubtSaveIds : [],
      order: serverChapter.order,
      pendingSync: stillPendingFull,
      syncPhase: stillPendingFull ? 'dirty' : 'synced_grace',
      lastSuccessfulSyncAt: now,
      lastErrorCode: undefined,
      lastErrorMessage: undefined,
      retained: false,
      patchScope: preserveLocalContent ? existing?.patchScope : undefined,
    })
    if (!stillPendingFull) {
      resetChapterNextVersionCounter(projectId, chapterId, serverVersion)
    }
    notifyListeners()
    return patched
  },

  markOfflineMirror(
    projectId: string,
    chapterId: string,
    serverChapter: Chapter,
  ): ChapterBackup | null {
    const saved = BackupService.saveSyncedChapterCopy({
      projectId,
      chapterId,
      title: serverChapter.title,
      content: serverChapter.content,
      jsonContent: serverChapter.jsonContent || {},
      baseUpdatedAt: serverChapter.updatedAt,
      baseContentVersion: readServerContentVersion(serverChapter),
      order: serverChapter.order,
    })
    if (saved) {
      BackupService.patchChapterBackup(projectId, chapterId, { patchScope: undefined })
      resetChapterNextVersionCounter(
        projectId,
        chapterId,
        readServerContentVersion(serverChapter),
      )
    }
    notifyListeners()
    return saved
      ? BackupService.getChapterBackup(projectId, chapterId)
      : null
  },

  async uploadChapter(
    backup: ChapterBackup | null,
    options: UploadOptions = {},
  ): Promise<UploadChapterResult> {
    if (!backup) {
      return { status: 'failed', error: new Error('missing backup'), message: 'No hay respaldo local.' }
    }

    if (!isBackupPendingUpload(backup as ChapterBackup & { syncPhase?: ChapterSyncPhase })) {
      return { status: 'noop' }
    }

    let workingBackup = backup

    const phase = resolveSyncPhase(workingBackup as ChapterBackup & { syncPhase?: ChapterSyncPhase })
    if (isBackgroundUploadBlockedPhase(phase)) {
      return { status: 'noop' }
    }

    if (
      isActiveEditorChapter(workingBackup.projectId, workingBackup.chapterId)
      && !options.allowWhileActiveEditor
    ) {
      return { status: 'noop' }
    }

    if (shouldRunStrictBackgroundVersionGuard(workingBackup, options)) {
      const strictCheck = await detectStrictBackgroundContentConflict(workingBackup)
      if (strictCheck.kind === 'conflict') {
        markBackupPossibleVersionConflict(
          workingBackup.projectId,
          workingBackup.chapterId,
        )
        return { status: 'conflict', serverChapter: strictCheck.serverChapter }
      }
      if (strictCheck.kind !== 'ok') {
        return handleStrictContentCheckFailure(workingBackup, strictCheck)
      }
    }

    const timestampAtStart = workingBackup.timestamp

    const performPatch = async (): Promise<UploadChapterResult> => {
      BackupService.patchChapterBackup(workingBackup.projectId, workingBackup.chapterId, {
        syncPhase: 'uploading',
        lastErrorCode: undefined,
        lastErrorMessage: undefined,
      })
      notifyListeners()

      const titleOnlyUpload = isTitleOnlyUpload(workingBackup, options)
      const saveId = options.saveId ?? createChapterSaveId()
      if (!titleOnlyUpload) {
        BackupService.patchChapterBackup(workingBackup.projectId, workingBackup.chapterId, {
          inDoubtSaveIds: appendInDoubtSaveId(workingBackup.inDoubtSaveIds, saveId),
        })
      }
      const latestBeforePatch = BackupService.getChapterBackup(
        workingBackup.projectId,
        workingBackup.chapterId,
      )
      const backupForPayload = latestBeforePatch ?? workingBackup
      const patchPayload = buildPatchPayload(backupForPayload, options, saveId, titleOnlyUpload)

      try {
        const response = await ChaptersService.patch(
          workingBackup.chapterId,
          patchPayload,
          { silentConnectivityError: options.silentConnectivityError ?? true },
        )
        const savedChapter = response.data as Chapter
        const latest = BackupService.getChapterBackup(workingBackup.projectId, workingBackup.chapterId)
        const backupMoved = Boolean(latest && latest.timestamp !== timestampAtStart)
        const ackedVersion = readServerContentVersion(savedChapter)

        const intendedVersion = typeof patchPayload.nextVersion === 'number'
          ? patchPayload.nextVersion
          : null
        if (
          !titleOnlyUpload
          && shouldRunStrictBackgroundVersionGuard(
            { ...workingBackup, timestamp: timestampAtStart },
            options,
          )
          && intendedVersion !== null
          && ackedVersion !== intendedVersion
        ) {
          markBackupPossibleVersionConflict(
            workingBackup.projectId,
            workingBackup.chapterId,
          )
          return {
            status: 'conflict',
            serverChapter: savedChapter,
          }
        }

        if (backupMoved) {
          BackupService.patchChapterBackup(workingBackup.projectId, workingBackup.chapterId, {
            syncPhase: 'dirty',
            baseUpdatedAt: savedChapter.updatedAt,
            baseContentVersion: ackedVersion,
          })
          resetChapterNextVersionCounter(backup.projectId, backup.chapterId, ackedVersion)
          notifyListeners()
          if (options.allowWhileActiveEditor) {
            enqueueForegroundUploadRetry(backup.projectId, backup.chapterId)
          } else {
            scheduleWorker(0)
          }
          return { status: 'editor_moved', chapter: savedChapter }
        }

        const offlineProject = BackupService.isProjectAvailableOffline(backup.projectId)
        if (offlineProject) {
          chapterPersistenceOrchestrator.markOfflineMirror(
            backup.projectId,
            backup.chapterId,
            savedChapter,
          )
        } else {
          chapterPersistenceOrchestrator.markSyncedGrace(
            backup.projectId,
            backup.chapterId,
            savedChapter,
            { preserveLocalContent: titleOnlyUpload },
          )
        }
        resetWorkerState(backup.projectId, backup.chapterId)
        notifyListeners()
        noteSuccessfulChapterSave(backup.projectId, backup.chapterId, savedChapter)
        return { status: 'success', chapter: savedChapter }
      } catch (error) {
        const status = getHttpErrorStatus(error)

        if (status === 401 || status === 403) {
          markWorkerExhausted(backup.projectId, backup.chapterId)
          BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
            syncPhase: 'auth_blocked',
            lastErrorCode: status === 401 ? 'UNAUTHORIZED_ERROR' : 'FORBIDDEN_ERROR',
            lastErrorMessage: describeChapterSyncFailure(error),
          })
          notifyListeners()
          return { status: 'auth_error' }
        }

        if (isChapterContentDeletionRequiresForceError(error)) {
          BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
            syncPhase: 'needs_editor',
            lastErrorCode: 'CHAPTER_CONTENT_DELETION_REQUIRES_FORCE',
            lastErrorMessage: describeChapterSyncFailure(error),
          })
          notifyListeners()
          return { status: 'needs_editor' }
        }

        if (isChapterVersionConflictError(error)) {
          const serverChapter = getChapterFromVersionConflictError(error)
          const latestBackup = BackupService.getChapterBackup(backup.projectId, backup.chapterId)
          const lostSaveId = readServerLastContentSaveId(serverChapter)
          if (
            serverChapter
            && lostSaveId
            && latestBackup?.inDoubtSaveIds?.includes(lostSaveId)
            && !options.retryingLostAck
          ) {
            const serverVersion = readServerContentVersion(serverChapter)
            BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
              baseContentVersion: serverVersion,
            })
            return chapterPersistenceOrchestrator.uploadChapter(latestBackup, {
              ...options,
              retryingLostAck: true,
              saveId: createChapterSaveId(),
            })
          }

          BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
            syncPhase: 'conflict',
            lastErrorCode: 'CONFLICT_ERROR',
            lastErrorMessage: describeChapterSyncFailure(error),
          })
          if (serverChapter) {
            logChapterContentVersionConflict(
              'patch',
              typeof patchPayload.nextVersion === 'number'
                ? patchPayload.nextVersion
                : normalizeContentVersion(backupForPayload.baseContentVersion),
              readServerContentVersion(serverChapter),
            )
          }
          notifyListeners()
          return {
            status: 'conflict',
            serverChapter: serverChapter || undefined,
          }
        }

        if (isAmbiguousSaveFailure(error)) {
          return handleAmbiguousPatchFailure(backup, error, options)
        }

        if (isConnectivityError(error)) {
          recordFailedAttempt(backup.projectId, backup.chapterId)
          const connectivityState = getWorkerState(backup.projectId, backup.chapterId)
          const connectivityMessage = describeChapterSyncFailure(error)
          const offlinePhase = !isAppOnline()
          BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
            syncPhase: offlinePhase
              ? 'offline'
              : (connectivityState.exhausted ? 'failed' : 'dirty'),
            lastErrorCode: 'CONNECTIVITY',
            lastErrorMessage: connectivityMessage,
          })
          notifyListeners()
          if (offlinePhase) {
            return { status: 'offline' }
          }
          if (options.allowWhileActiveEditor) {
            enqueueForegroundUploadRetry(backup.projectId, backup.chapterId)
          } else {
            scheduleWorker(getBackoffDelayMs(connectivityState))
          }
          return {
            status: 'failed',
            error,
            message: connectivityMessage,
          }
        }

        recordFailedAttempt(backup.projectId, backup.chapterId)
        BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
          syncPhase: 'failed',
          lastErrorCode: status ? String(status) : undefined,
          lastErrorMessage: describeChapterSyncFailure(error),
        })
        notifyListeners()
        scheduleWorker(getBackoffDelayMs(getWorkerState(backup.projectId, backup.chapterId)))
        return { status: 'failed', error, message: describeChapterSyncFailure(error) }
      }
    }

    const lockName = `chapter-save:${backup.projectId}:${backup.chapterId}`
    const uploadKey = chapterKey(backup.projectId, backup.chapterId)
    incrementInFlightUpload(uploadKey)
    try {
      if ('locks' in navigator && typeof navigator.locks.request === 'function') {
        let lockAcquired = false
        try {
          return await navigator.locks.request(lockName, { ifAvailable: true }, async (lock) => {
            if (!lock) {
              BackupService.patchChapterBackup(backup.projectId, backup.chapterId, {
                syncPhase: 'dirty',
              })
              notifyListeners()
              if (options.allowWhileActiveEditor) {
                enqueueForegroundUploadRetry(backup.projectId, backup.chapterId)
              } else {
                scheduleWorker(LOCK_BUSY_RETRY_MS)
              }
              if (
                typeof navigator !== 'undefined'
                && navigator.onLine
                && shouldCaptureChapterSaveDiagnostic(backup.chapterId)
              ) {
                captureChapterSaveDiagnostic('lock_busy', {
                  chapterId: backup.chapterId,
                  projectId: backup.projectId,
                  navigatorOnLine: true,
                })
              }
              return {
                status: 'failed',
                error: new Error('lock busy'),
                message: 'Guardado en curso.',
              }
            }
            lockAcquired = true
            return await performPatch()
          })
        } catch (error) {
          if (lockAcquired) {
            throw error
          }
          return await performPatch()
        }
      }

      return await performPatch()
    } finally {
      decrementInFlightUpload(uploadKey)
    }
  },
}

const debouncedPersistByChapter = new Map<string, ReturnType<typeof debounce>>()

export function debouncedPersistLocalDraft(
  input: PersistLocalDraftInput,
  delayMs = LOCAL_PERSIST_DEBOUNCE_MS,
): void {
  const key = chapterKey(input.projectId, input.chapterId)
  let fn = debouncedPersistByChapter.get(key)
  if (!fn) {
    fn = debounce((draft: PersistLocalDraftInput) => {
      chapterPersistenceOrchestrator.persistLocalDraft(draft)
    }, delayMs, { maxWait: LOCAL_PERSIST_DEBOUNCE_MAX_WAIT_MS })
    debouncedPersistByChapter.set(key, fn)
  }
  fn(input)
}

export function flushDebouncedPersist(projectId: string, chapterId: string): void {
  const key = chapterKey(projectId, chapterId)
  const fn = debouncedPersistByChapter.get(key)
  fn?.flush()
}

export function cancelDebouncedPersist(projectId: string, chapterId: string): void {
  const key = chapterKey(projectId, chapterId)
  const fn = debouncedPersistByChapter.get(key)
  fn?.cancel()
}

function cancelAllDebouncedPersist(): void {
  for (const fn of debouncedPersistByChapter.values()) {
    fn.cancel()
  }
  debouncedPersistByChapter.clear()
}

/** Resets upload worker state and cancels pending local persist debounces (e.g. on logout). */
export function resetChapterPersistenceSession(): void {
  resetChapterPersistenceOrchestratorForTests()
  clearAuthReleaseSessionTracking()
  cancelAllDebouncedPersist()
  notifyListeners()
}

export function bootChapterPersistenceOrchestrator(): void {
  installOnlineWatcher()
  void BackupService.whenReady().then(() => {
    chapterPersistenceOrchestrator.reconcile()
  })
}
